<template>
  <AppPane :is-open="checklistPane.isOpen"
           :tabs="displayTabs"
           :navigate-previous="previousItem"
           :navigate-previous-label="$t(`${TRANSLATION_KEY_PREFIX}.navigatePrevious`)"
           :navigate-next="nextItem"
           :navigate-next-label="$t(`${TRANSLATION_KEY_PREFIX}.navigateNext`)"
           scrim="transparent"
           v-model:current-tab="tab"
           data-locator="checklist-task-pane"
           @next-item="onNextItem"
           @previous-item="onPreviousItem"
           @update:modelValue="onClosePane"
  >
    <template #header>
      <div class="mx-6" data-locator="task-title">
        <h2>{{ displayPaneTitle }}</h2>
      </div>
    </template>
    <template #content-details>
      <ChecklistPaneDetails @data-unsaved="onDataUnsaved" />
    </template>
    <template #content-files>
      <ChecklistPaneFiles />
    </template>
    <template #content-comments>
      <ChecklistPaneComments @data-unsaved="onDataUnsaved" />
    </template>
    <template #content-rights>
      <ChecklistPaneRights />
    </template>
    <template #footer>
      <ChecklistPaneDataUnsavedDialog 
        v-if="isChecklistPaneDataUnsavedDialogOpen"
        :tab="tabWithDataUnsaved" 
        @close="onCloseDialog"
        @confirm="onConfirmClosePane" 
      />
    </template>
  </AppPane>
</template>

<script>
import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AppPane from '@/common/AppPane.vue'
import ChecklistPaneDataUnsavedDialog from '@/project/checklist/dialogs/ChecklistPaneDataUnsavedDialog.vue'
import ChecklistPaneComments from '@/project/checklist/panes/ChecklistPaneComments.vue'
import ChecklistPaneDetails from '@/project/checklist/panes/ChecklistPaneDetails.vue'
import ChecklistPaneFiles from '@/project/checklist/panes/ChecklistPaneFiles.vue'
import ChecklistPaneRights from '@/project/checklist/panes/ChecklistPaneRights.vue'
import { CHANGE_CHECKLIST_PANE_TASKS, CLOSE_CHECKLIST_PANE, GET_CHECKLIST, GET_TASK_DETAILS } from '@/store/modules/checklist/action_types'
import { SET_CHECKLIST_PANE_TAB } from '@/store/modules/checklist/mutation_types'

const TRANSLATION_KEY_PREFIX = 'project.checklist.panes.ChecklistPane'

export default defineComponent({
  name: 'ChecklistPane',
  components: { 
    AppPane,
    ChecklistPaneDataUnsavedDialog,
    ChecklistPaneComments,
    ChecklistPaneDetails,
    ChecklistPaneFiles,
    ChecklistPaneRights,
  },
  data () {
    return {
      TRANSLATION_KEY_PREFIX,
      hasNavigateBetweenTasks: false,
      isChecklistPaneDataUnsavedDialogOpen: false,
      tabWithDataUnsaved: null,
      tabs: [
        {
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.tabDetails`),
          id: 'details',
        },
        {
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.tabFiles`),
          id: 'files',
        },
        {
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.tabComments`),
          id: 'comments',
        },
        {
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.tabRights`),
          id: 'rights',
        },
      ],
    }
  },
  computed: {
    ...mapState('checklist', ['checklistPane']),
    ...mapGetters('room', ['isCurrentUserPm', 'roomMnemo']),
    displayPaneTitle() {
      return this.currentTask ? `${ this.currentTask?.positionDisplay || '' } ${ this.currentTask?.title || '' }` : ''
    },
    displayTabs() {
      return this.isCurrentUserPm ? this.tabs : this.tabs.filter((tab) => tab.id !== 'rights')
    },
    currentTask() {
      return this.checklistPane.tasks[0]
    },
    nextItem() {
      return this.getSiblingTask('next')
    },
    previousItem() {
      return this.getSiblingTask('prev')
    },
    tab: {
      get () {
        return this.checklistPane.tab
      },
      set (val) {
        this.SET_CHECKLIST_PANE_TAB(val)
      },
    },
  },
  methods: {
    ...mapActions('checklist', [ CHANGE_CHECKLIST_PANE_TASKS, CLOSE_CHECKLIST_PANE, GET_CHECKLIST, GET_TASK_DETAILS ]),
    ...mapMutations('checklist', [ SET_CHECKLIST_PANE_TAB ]),
    getSiblingTask(direction) {
      const parentTasks = this.checklistPane.parent?.children.filter(item => item.type === 'task') || []
      const currentTaskIndex = parentTasks.findIndex(task => task.id === this.currentTask?.id)

      if (currentTaskIndex === -1) return null

      const siblingIndex = direction === 'next' ? currentTaskIndex + 1 : currentTaskIndex - 1
      return parentTasks[siblingIndex] || null
    },
    onCloseDialog () {
      this.isChecklistPaneDataUnsavedDialogOpen = false
    },
    onClosePane () {
      if(this.tabWithDataUnsaved) {
        this.isChecklistPaneDataUnsavedDialogOpen = true
      } else {
        this.refreshChecklistData()
        this.CLOSE_CHECKLIST_PANE()
      }
    },
    onConfirmClosePane () {
      this.tabWithDataUnsaved = null
      this.onCloseDialog()
      this.refreshChecklistData()
      this.CLOSE_CHECKLIST_PANE()
    },
    onDataUnsaved (dataUnsaved) {
      this.tabWithDataUnsaved = dataUnsaved
    },
    onNextItem() {
      if(this.nextItem) {
        this.CHANGE_CHECKLIST_PANE_TASKS([this.nextItem])
        this.hasNavigateBetweenTasks = true
      }
    },
    onPreviousItem() {
      if(this.previousItem) {
        this.CHANGE_CHECKLIST_PANE_TASKS([this.previousItem])
        this.hasNavigateBetweenTasks = true
      }
    },
    async refreshChecklistData() {
      if (!this.hasNavigateBetweenTasks) {
        await this.GET_TASK_DETAILS({
          mnemo: this.roomMnemo,
          taskId: this.currentTask.id,
        })
      } else {
        await this.GET_CHECKLIST(this.roomMnemo)
      }
      
      this.hasNavigateBetweenTasks = false
    },
  },
})
</script>
